import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Clock, CheckCircle, XCircle,
  TrendingUp, Users, FileText, Loader2, RefreshCw, ArrowRight,
  BarChart3, PieChart
} from 'lucide-react';

function ClaimsDashboard() {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/imports/claims/dashboard`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load claims dashboard:', err);
    }
    setLoading(false);
  };

  // Format a split-currency amount object {USD: x, INR: y} as a display string
  const fmtSplit = (amtObj) => {
    if (!amtObj || typeof amtObj !== 'object') return '₹0';
    const parts = [];
    if (amtObj.USD > 0) parts.push(`$${amtObj.USD.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
    if (amtObj.INR > 0) parts.push(`₹${amtObj.INR.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
    return parts.length > 0 ? parts.join(' + ') : '₹0';
  };

  // Format individual amounts with their currency
  const fmtCur = (amount, currency) => {
    const sym = currency === 'USD' ? '$' : '₹';
    return `${sym}${(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  // Format combined USD+INR from a single object with amountUSD and amountINR
  const fmtDual = (obj) => {
    const parts = [];
    if ((obj?.amountUSD || 0) > 0) parts.push(`$${obj.amountUSD.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
    if ((obj?.amountINR || 0) > 0) parts.push(`₹${obj.amountINR.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
    return parts.length > 0 ? parts.join(' + ') : '₹0';
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-gray-500">
        Failed to load dashboard data.
        <button onClick={loadDashboard} className="ml-3 text-amber-600 underline">Retry</button>
      </div>
    );
  }

  const { summary, statusBreakdown, agingAnalysis, typeBreakdown, vendorBreakdown, recentPending, monthlyTrend } = data;

  const statusColors = {
    'Pending': { bg: 'bg-amber-100', text: 'text-amber-700', bar: 'bg-amber-500' },
    'Under Review': { bg: 'bg-blue-100', text: 'text-blue-700', bar: 'bg-blue-500' },
    'Approved': { bg: 'bg-emerald-100', text: 'text-emerald-700', bar: 'bg-emerald-500' },
    'Rejected': { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' },
    'Settled': { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-500' },
    'Closed': { bg: 'bg-gray-100', text: 'text-gray-700', bar: 'bg-gray-500' },
  };

  const agingBarColors = {
    '0-30 days': 'bg-emerald-500',
    '31-60 days': 'bg-amber-500',
    '61-90 days': 'bg-orange-500',
    '90+ days': 'bg-red-500',
  };

  const maxStatusCount = Math.max(...Object.values(statusBreakdown).map(s => s.count), 1);
  const maxAgingCount = Math.max(...Object.values(agingAnalysis).map(a => a.count), 1);

  return (
    <div className="p-6 space-y-6" data-testid="claims-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
              <BarChart3 className="text-white" size={24} />
            </div>
            Claims Dashboard
          </h1>
          <p className="text-gray-500 text-sm mt-1">Overview of import claims, aging, and vendor analysis</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadDashboard} className="p-2 hover:bg-gray-100 rounded-lg transition" title="Refresh">
            <RefreshCw size={20} className={loading ? 'animate-spin text-gray-400' : 'text-gray-600'} />
          </button>
          <button
            onClick={() => navigate('/imports/claims')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl hover:from-amber-700 hover:to-orange-700 transition font-medium text-sm"
            data-testid="go-to-claims-btn"
          >
            <FileText size={16} />
            View All Claims
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="claims-summary-cards">
        <SummaryCard
          label="Total Claims"
          count={summary.totalClaims}
          amount={fmtSplit(summary.totalAmount)}
          icon={<AlertTriangle size={22} />}
          iconBg="bg-slate-100"
          iconColor="text-slate-600"
          border="border-slate-200"
        />
        <SummaryCard
          label="Pending"
          count={summary.pendingCount}
          amount={fmtSplit(summary.pendingAmount)}
          icon={<Clock size={22} />}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          border="border-amber-200"
          highlight
        />
        <SummaryCard
          label="Settled / Closed"
          count={summary.settledCount}
          amount={fmtSplit(summary.settledAmount)}
          icon={<CheckCircle size={22} />}
          iconBg="bg-green-100"
          iconColor="text-green-600"
          border="border-green-200"
        />
        <SummaryCard
          label="Rejected"
          count={summary.rejectedCount}
          amount={fmtSplit(summary.rejectedAmount)}
          icon={<XCircle size={22} />}
          iconBg="bg-red-100"
          iconColor="text-red-600"
          border="border-red-200"
        />
      </div>

      {/* Row 2: Status Breakdown + Aging Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="status-breakdown">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <PieChart size={16} className="text-blue-500" />
            Status Breakdown
          </h3>
          <div className="space-y-3">
            {Object.entries(statusBreakdown).map(([status, info]) => {
              const colors = statusColors[status] || statusColors['Pending'];
              const pct = Math.round((info.count / maxStatusCount) * 100);
              return (
                <div key={status} className="group">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                      {status}
                    </span>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="font-semibold text-gray-800">{info.count}</span>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-600">{fmtDual(info)}</span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${colors.bar} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {Object.keys(statusBreakdown).length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">No claims data available</p>
            )}
          </div>
        </div>

        {/* Aging Analysis */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="aging-analysis">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Clock size={16} className="text-orange-500" />
            Aging Analysis (Open Claims)
          </h3>
          <div className="space-y-4">
            {Object.entries(agingAnalysis).map(([bucket, info]) => {
              const barColor = agingBarColors[bucket] || 'bg-gray-400';
              const pct = maxAgingCount > 0 ? Math.round((info.count / maxAgingCount) * 100) : 0;
              return (
                <div key={bucket}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-700">{bucket}</span>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full text-xs font-medium">{info.count} claims</span>
                      <span className="font-semibold text-gray-800">{fmtDual(info)}</span>
                    </div>
                  </div>
                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, info.count > 0 ? 4 : 0)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Total Open</span>
              <span className="font-bold text-gray-800">
                {Object.values(agingAnalysis).reduce((s, a) => s + a.count, 0)} claims
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Monthly Trend + Type Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="monthly-trend">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-indigo-500" />
            Monthly Trend (Last 6 Months)
          </h3>
          {monthlyTrend.length > 0 ? (
            <div className="space-y-3">
              {monthlyTrend.map((m) => {
                const monthLabel = new Date(m.month + '-01').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
                return (
                  <div key={m.month} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600 w-16">{monthLabel}</span>
                    <div className="flex-1">
                      <div className="flex gap-1 h-5">
                        {m.amountUSD > 0 && (
                          <div className="bg-blue-500 rounded h-full text-[9px] text-white flex items-center justify-center px-1.5 min-w-[30px]"
                            title={`$${m.amountUSD.toLocaleString('en-IN')}`}>
                            ${m.amountUSD.toLocaleString('en-IN', {maximumFractionDigits: 0})}
                          </div>
                        )}
                        {m.amountINR > 0 && (
                          <div className="bg-indigo-500 rounded h-full text-[9px] text-white flex items-center justify-center px-1.5 min-w-[30px]"
                            title={`₹${m.amountINR.toLocaleString('en-IN')}`}>
                            ₹{m.amountINR.toLocaleString('en-IN', {maximumFractionDigits: 0})}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 w-16 text-right">{m.count} claims</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">No trend data available</p>
          )}
        </div>

        {/* Claim Type Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="type-breakdown">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <FileText size={16} className="text-purple-500" />
            Claim Types
          </h3>
          <div className="space-y-3">
            {Object.entries(typeBreakdown).map(([type, info]) => {
              const pct = summary.totalClaims > 0 ? Math.round((info.count / summary.totalClaims) * 100) : 0;
              return (
                <div key={type}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700 truncate max-w-[140px]" title={type}>{type}</span>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-gray-800">{info.count}</span>
                      <span className="text-gray-400 text-xs">({pct}%)</span>
                      <span className="text-gray-500 text-xs">{fmtDual(info)}</span>
                    </div>
                  </div>
                  <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 4)}%` }} />
                  </div>
                </div>
              );
            })}
            {Object.keys(typeBreakdown).length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">No claims data</p>
            )}
          </div>
        </div>
      </div>

      {/* Row 4: Vendor Breakdown + Recent Pending */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendor/Party-wise */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="vendor-breakdown">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Users size={16} className="text-teal-500" />
            Top Vendors by Claim Amount
          </h3>
          {vendorBreakdown.length > 0 ? (
            <div className="space-y-3">
              {vendorBreakdown.map((v, i) => {
                const totalForBar = (v.totalAmountUSD || 0) + (v.totalAmountINR || 0);
                const maxTotal = (vendorBreakdown[0]?.totalAmountUSD || 0) + (vendorBreakdown[0]?.totalAmountINR || 0);
                const pct = maxTotal > 0 ? Math.round((totalForBar / maxTotal) * 100) : 0;
                const amtParts = [];
                if (v.totalAmountUSD > 0) amtParts.push(`$${v.totalAmountUSD.toLocaleString('en-IN', {maximumFractionDigits: 0})}`);
                if (v.totalAmountINR > 0) amtParts.push(`₹${v.totalAmountINR.toLocaleString('en-IN', {maximumFractionDigits: 0})}`);
                const amtStr = amtParts.length > 0 ? amtParts.join(' + ') : '₹0';
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-700 font-medium truncate max-w-[200px]" title={v.party}>{v.party}</span>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-xs text-gray-400">{v.count} claims</span>
                        <span className="font-semibold text-gray-800">{amtStr}</span>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 4)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">No vendor data</p>
          )}
        </div>

        {/* Recent Pending Claims */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="recent-pending-claims">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-500" />
            Recent Pending Claims
          </h3>
          {recentPending.length > 0 ? (
            <div className="space-y-2.5 max-h-72 overflow-y-auto">
              {recentPending.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-amber-50/60 border border-amber-100 rounded-lg hover:bg-amber-50 transition">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-amber-800">{c.claimNumber}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[c.status]?.bg || 'bg-gray-100'} ${statusColors[c.status]?.text || 'text-gray-600'}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {c.partyName} &middot; {c.claimType} &middot; {fmtDate(c.claimDate)}
                    </div>
                  </div>
                  <div className="text-right ml-3">
                    <div className="text-sm font-bold text-gray-800">{fmtCur(c.claimAmount, c.currency)}</div>
                    {c.shortageQty > 0 && (
                      <div className="text-[10px] text-gray-400">{c.shortageQty.toFixed(3)} MT shortage</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle size={32} className="mx-auto text-green-400 mb-2" />
              <p className="text-sm text-gray-500">No pending claims</p>
            </div>
          )}
          {recentPending.length > 0 && (
            <button
              onClick={() => navigate('/imports/claims')}
              className="mt-3 w-full flex items-center justify-center gap-2 py-2 text-sm text-amber-700 hover:bg-amber-100 rounded-lg transition font-medium"
              data-testid="view-all-claims-link"
            >
              View All Claims <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, count, amount, icon, iconBg, iconColor, border, highlight }) {
  return (
    <div className={`p-4 rounded-xl border-2 ${border} ${highlight ? 'ring-2 ring-amber-200 bg-amber-50/30' : 'bg-white'} transition hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 font-medium">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
          <p className="text-xs text-gray-500 mt-1">{amount}</p>
        </div>
        <div className={`p-2.5 ${iconBg} rounded-full ${iconColor}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

export default ClaimsDashboard;
